import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCallback, useEffect, useState } from "react";
import service from "../Helpers/gifservices";
import { setData, setServiceFunction } from "../../../store/formSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  canEditSelector,
  refereeSelector,
  refereeStatusSelector,
  setRefereeStatus,
} from "../../../store/refereeSlice";
import { useLoading } from "../../../providers/loadingProvider";
import { useNotify } from "../../../providers/NotificationProvider";
import { checkGrading } from "../Helpers/common";
import { getGrades, GradesKeys } from "../../../Common/gradeWeights";

const pageName = "Grading";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  totalTypography: {
    fontSize: 16,
    fontWeight: 600,
  },
  totalRow: {
    backgroundColor: "#D0D0D0",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const GradingRow = (props) => {
  const { weightedGrade, setItem, name, weight } = props;
  const notify = useNotify();
  const canEdit = useSelector(canEditSelector);
  const [grade, setGrade] = useState(0);
  const [weighted, setWeighted] = useState(0);
  const [gardeCalculateFinish, setGardeCalculateFinish] = useState(false);

  const valueChange = (e) => {
    var grade = e.target.value;
    if (grade > 100 || grade < 0) {
      notify.show("Grade Needs to be Between 0-100", { severity: "error" });
      return;
    }
    setGrade(grade);
    var weightedGrade = (grade / 100) * weight;
    weightedGrade = parseFloat(weightedGrade.toFixed(1));
    setWeighted(weightedGrade);
  };

  useEffect(() => {
    if (weightedGrade === null) return;
    if (!gardeCalculateFinish) {
      setWeighted(weightedGrade);
      const grade = Math.round((weightedGrade / weight) * 100);
      setGrade(grade);
      setGardeCalculateFinish(true);
    }
  }, [weightedGrade, weight, gardeCalculateFinish]);

  return (
    <TableRow>
      <TableCell>{name}</TableCell>
      <TableCell>{weight}</TableCell>
      <TableCell>
        <TextField
          value={grade}
          disabled={!canEdit}
          type="number"
          onChange={valueChange}
          onBlur={() => setItem(weighted)}
        />
      </TableCell>
      <TableCell>{weighted}</TableCell>
    </TableRow>
  );
};

const Grading = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const loading = useLoading();
  const [dataTotal, setDataTotal] = useState({});
  const [proposalReferee, setProposalReferee] = useState({});
  const referee = useSelector(refereeSelector);
  const refereeStatus = useSelector(refereeStatusSelector);
  const [proposalRefereeId, setProposalRefereeId] = useState(null);
  const [grades, setGrades] = useState([]);
  const tableHeaders = [
    { label: "Evaluation item", size: "39%" },
    { label: "Weight (%)", size: "19%" },
    { label: "Your Grade", size: "19%" },
    { label: "Weighted grade", size: "19%" },
  ];

  useEffect(() => {
    setProposalRefereeId(referee?.id);
  }, [referee]);

  const GetProposalReferee = useCallback(async () => {
    if (!proposalRefereeId) return;
    const loadingId = loading.addLoading("Loading Proposal");

    var proposalreferee = await service.GetProposalRefereeById({
      proposalRefereeId,
    });
    setProposalReferee(proposalreferee);
    const grades = getGrades(
      proposalreferee.originalityInnovation,
      proposalreferee.adequacyOfMethods,
      proposalreferee.suitabilityOfInvestigatorsScientific,
      proposalreferee.modeOfCooperationBetweenTheTeams
    );

    setGrades(grades);
    loading.removeLoading(loadingId);
  }, [proposalRefereeId, loading]);

  const getProposalRefereeWithGrades = (proposalReferee, grades) => {
    const getGrade = (key) =>
      parseFloat(grades.find((g) => g.key === key)?.weightedGrade || 0);
    return {
      ...proposalReferee,
      originalityInnovation: getGrade(GradesKeys.ORIGINALITY),
      adequacyOfMethods: getGrade(GradesKeys.PUBLICATIONS),
      suitabilityOfInvestigatorsScientific: getGrade(GradesKeys.SUITABILLY),
      modeOfCooperationBetweenTheTeams: getGrade(GradesKeys.COOPERATION),
    };
  };
  const setGrade = (grade, key) => {
    const newpr = getProposalRefereeWithGrades(proposalReferee, grades);
    switch (key) {
      case GradesKeys.ORIGINALITY:
        newpr.originalityInnovation = grade;
        break;
      case GradesKeys.PUBLICATIONS:
        newpr.adequacyOfMethods = grade;
        break;
      case GradesKeys.SUITABILLY:
        newpr.suitabilityOfInvestigatorsScientific = grade;
        break;
      case GradesKeys.COOPERATION:
        newpr.modeOfCooperationBetweenTheTeams = grade;
        break;
      default:
        throw new Error(`${key} is no defined`);
    }
    const newGrades = getGrades(
      newpr.originalityInnovation,
      newpr.adequacyOfMethods,
      newpr.suitabilityOfInvestigatorsScientific,
      newpr.modeOfCooperationBetweenTheTeams
    );
    setGrades(newGrades);
    dispatch(
      setData({
        proposalReferee: newpr,
      })
    );
  };

  useEffect(() => {
    dispatch(setServiceFunction(`UpdateRefereeRecommendation`));
  }, [dispatch]);

  useEffect(() => {
    GetProposalReferee();
  }, [proposalRefereeId, GetProposalReferee]);

  useEffect(() => {
    const totalWeightedGrade = grades.reduce(
      (sum, g) => sum + g.weightedGrade,
      0
    );
    setDataTotal({
      weight: 100,
      weightedGrade: parseFloat(totalWeightedGrade.toFixed(1)),
    });
  }, [grades]);

  useEffect(() => {
    if (!proposalReferee) return;
    const newProposalReferee = getProposalRefereeWithGrades(
      proposalReferee,
      grades
    );
    const gradingStatus = checkGrading(newProposalReferee);
    if (refereeStatus?.grading !== gradingStatus) {
      dispatch(setRefereeStatus({ ...refereeStatus, grading: gradingStatus }));
    }
  }, [proposalReferee, dispatch, refereeStatus, grades]);

  return (
    <>
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid item>
          <Typography variant="subtitle2">{pageName}</Typography>
        </Grid>
        <Grid item>
          <Typography>
            Please indicate your grading for each of the following criteria on a
            scale of 1 (low) to 100 (high)
            <br/>
            This part <b>will not</b> be sent to the applicant/s
          </Typography>
        </Grid>
      </Grid>
      <Paper className={classes.root}>
        <Table
          stickyHeader
          className={classes.table}
          aria-label="caption table"
        >
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell
                  className={classes.tableCell}
                  key={header.label}
                  align="left"
                  style={{ width: header.size }}
                >
                  {header.label}
                </TableCell>
              ))}
              <TableCell align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {grades.map((g, index) => (
              <GradingRow
                key={index}
                name={g.name}
                weight={g.weight}
                setItem={(grade) => setGrade(grade, g.key)}
                weightedGrade={g.weightedGrade}
              />
            ))}
            <TableRow className={classes.totalRow}>
              <TableCell>
                <Typography
                  variant="subtitle2"
                  className={classes.totalTypography}
                >
                  Total
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="subtitle2"
                  className={classes.totalTypography}
                >
                  {dataTotal.weight}
                </Typography>
              </TableCell>
              <TableCell></TableCell>
              <TableCell>
                <Typography
                  variant="subtitle2"
                  className={classes.totalTypography}
                >
                  {dataTotal.weightedGrade}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

export default Grading;
